import React from 'react';
import { Row, Col, Card, CardBody, CardTitle, Table, Button } from 'reactstrap';
import { BiaMethodDetails } from './BiaMethodDetails';

function fmt(value, decimals = 2) {
  if (value === undefined || value === null || !Number.isFinite(Number(value))) return '-';
  return Number(value).toFixed(decimals);
}

export function BiaResults({ measurement, composition, onExport, onNewMeasure }) {
  const raw = measurement || {};
  const comp = raw.processingIssue ? {} : composition || {};
  const raw5 = [
    { label: 'Impedenza (Z)', value: raw.impedance5 ?? raw.imp_5, unit: 'ohm' },
    { label: 'Angolo fase', value: raw.phase5 ?? raw.ang_5, unit: '\u00b0' },
    { label: 'Resistenza (R)', value: raw.resistance5 ?? raw.res_5, unit: 'ohm' },
    { label: 'Reattanza (Xc)', value: raw.reactance5 ?? raw.rea_5, unit: 'ohm' }
  ];
  const hasFive = raw.acquisitionProfile !== 'classic' && raw5.every(item => Number.isFinite(Number(item.value)) && Number(item.value) > 0);

  const raw50 = [
    { label: 'Impedenza (Z)', value: raw.impedance50 ?? raw.imp_50, unit: 'ohm' },
    { label: 'Angolo fase', value: raw.phase50 ?? raw.ang_50, unit: '\u00b0' },
    { label: 'Resistenza (R)', value: raw.resistance50 ?? raw.res_50, unit: 'ohm' },
    { label: 'Reattanza (Xc)', value: raw.reactance50 ?? raw.rea_50, unit: 'ohm' }
  ];

  const raw100 = [
    { label: 'Impedenza (Z)', value: raw.impedance100 ?? raw.imp_100, unit: 'ohm' },
    { label: 'Angolo fase', value: raw.phase100 ?? raw.ang_100, unit: '\u00b0' },
    { label: 'Resistenza (R)', value: raw.resistance100 ?? raw.res_100, unit: 'ohm' },
    { label: 'Reattanza (Xc)', value: raw.reactance100 ?? raw.rea_100, unit: 'ohm' }
  ];

  const compositionCards = [
    { label: 'BMI', value: comp.bmi ?? comp.BMI, decimals: 1, variant: '' },
    { label: 'Massa Magra (kg)', value: comp.ffm ?? comp.FFM, decimals: 1, variant: 'accent' },
    { label: 'Massa Grassa (kg)', value: comp.fm ?? comp.FM, decimals: 1, variant: 'warn' },
    { label: 'Massa Grassa (%)', value: comp.fM_pct ?? comp.fmPct ?? comp.FM_pct, decimals: 1, suffix: '%', variant: 'warn' },
    { label: 'Acqua Totale (L)', value: comp.tbw ?? comp.TBW, decimals: 1, variant: '' },
    { label: 'Acqua Extra (L)', value: comp.ecw ?? comp.ECW, decimals: 1, variant: '' },
    { label: 'Acqua Intra (L)', value: comp.icw ?? comp.ICW, decimals: 1, variant: '' },
    { label: 'Massa Muscolare (kg)', value: comp.mm ?? comp.MM, decimals: 1, variant: 'accent' },
    { label: 'Massa Cellulare (kg)', value: comp.bcm ?? comp.BCM, decimals: 1, variant: '' },
    { label: 'Metabolismo (kcal)', value: comp.bmr ?? comp.BMR, decimals: 0, variant: '' },
    { label: 'Na/K', value: comp.naK ?? comp.NaK, decimals: 3, variant: '' },
    { label: 'BCMI', value: comp.bcmi ?? comp.BCMI, decimals: 1, variant: '' }
  ];

  const renderRawTable = (items, frequency) => (
    <Table size="sm" className="bia-raw-table" aria-label={`Dati grezzi ${frequency} kHz`}>
      <tbody>
        {items.map((item, idx) => (
          <tr key={idx}>
            <td>{item.label}</td>
            <td className="text-end fw-bold">{fmt(item.value, 2)}</td>
            <td className="text-end text-muted">{item.unit}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  return (
    <Card className="bia-card">
      <CardBody>
        <CardTitle tag="h2">4. Risultati</CardTitle>
        <p>{hasFive ? 'Acquisizione multifrequenza · 5 / 50 / 100 kHz' : raw.acquisitionProfile === 'classic' ? 'Acquisizione classica · 50 / 100 kHz' : 'Frequenze disponibili riportate sotto; profilo storico non dichiarato.'}</p>

        <div className="mb-4">
          <h5 className="bia-section-title">Dati grezzi acquisiti</h5>
          <Row>
            <Col md="4" className="mb-3">
              <h6 className="bia-subtitle">5 kHz</h6>
              {hasFive ? renderRawTable(raw5, 5) : <p className="text-muted">{raw.acquisitionProfile === 'classic' ? 'Non acquisiti in modalità classica.' : 'Dati assenti o incompleti. Nessun valore stimato.'}</p>}
            </Col>
            <Col md="4" className="mb-3">
              <h6 className="bia-subtitle">50 kHz</h6>
              {renderRawTable(raw50, 50)}
            </Col>
            <Col md="4" className="mb-3">
              <h6 className="bia-subtitle">100 kHz</h6>
              {renderRawTable(raw100, 100)}
            </Col>
          </Row>
        </div>

        <div className="mb-4">
          <h5 className="bia-section-title">Composizione corporea</h5>
          <Row className="g-2">
            {compositionCards.map((card, idx) => (
              <Col xs="6" sm="4" md="3" key={idx}>
                <div className={`bia-comp-card ${card.variant}`}>
                  <div className="bia-comp-value">{fmt(card.value, card.decimals)}{card.suffix || ''}</div>
                  <div className="bia-comp-label">{card.label}</div>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        <BiaMethodDetails measurement={raw} />

        <div className="d-flex gap-2 flex-wrap">
          <Button color="primary" onClick={onExport}>Esporta JSON</Button>
          <Button color="secondary" outline onClick={onNewMeasure}>Nuova misura</Button>
        </div>
      </CardBody>
    </Card>
  );
}

export default BiaResults;
